import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import DataTable from '@/components/admin/DataTable';
import CourseResources from '@/components/course/CourseResources';
import {
  ConfirmDeleteDialog,
  InlineSelect,
  courseLabel,
  formatDate,
  publishStatusOptions,
} from '@/components/admin/AcademicTableHelpers';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { createAssignment, deleteAssignment, getAssignments, getCourses, getMyAssignments, getMyCourses, updateAssignment } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { FileText, Pencil, Plus, Trash2 } from 'lucide-react';

function filterParam(value) {
  return value === 'all' ? undefined : value;
}

export default function ManageAssignments({ instructorMode = false }) {
  const { loading: authLoading } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [courseFilter, setCourseFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [savingInline, setSavingInline] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    course_id: '',
    due_date: '',
    total_marks: 100,
    instructions: '',
    status: 'draft',
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [assignmentRes, courseRes] = await Promise.all([
        instructorMode ? getMyAssignments() : getAssignments({ course_id: filterParam(courseFilter), status: filterParam(statusFilter) }),
        instructorMode ? getMyCourses() : getCourses(),
      ]);
      const nextAssignments = assignmentRes.data || [];
      setAssignments(nextAssignments);
      setCourses(courseRes.data || []);
      setSelectedIds((prev) => {
        const visible = new Set(nextAssignments.map((assignment) => assignment.id));
        return new Set([...prev].filter((id) => visible.has(id)));
      });
    } catch (err) {
      console.error(err);
      toast.error('Could not load assignments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, courseFilter, statusFilter]);

  const courseMap = useMemo(() => {
    const map = {};
    courses.forEach((course) => { map[course.id] = course; });
    return map;
  }, [courses]);

  const filteredAssignments = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return assignments;
    return assignments.filter((assignment) => {
      const course = courseMap[assignment.course_id];
      return (
        (assignment.title || '').toLowerCase().includes(query)
        || courseLabel(course).toLowerCase().includes(query)
      );
    });
  }, [assignments, courseMap, search]);

  const selectedAssignments = useMemo(
    () => assignments.filter((assignment) => selectedIds.has(assignment.id)),
    [assignments, selectedIds],
  );

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', description: '', course_id: '', due_date: '', total_marks: 100, instructions: '', status: 'draft' });
    setShowDialog(true);
  };

  const openEdit = (assignment) => {
    setEditing(assignment);
    setForm({
      title: assignment.title || '',
      description: assignment.description || '',
      course_id: assignment.course_id || '',
      due_date: assignment.due_date || '',
      total_marks: assignment.total_marks || 100,
      instructions: assignment.instructions || '',
      status: assignment.status || 'draft',
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.course_id) {
      toast.error('Title and course are required');
      return;
    }
    try {
      if (editing) {
        await updateAssignment(editing.id, form);
        toast.success('Assignment updated');
      } else {
        await createAssignment(form);
        toast.success('Assignment created');
      }
      setShowDialog(false);
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to save assignment');
    }
  };

  const handleInlineStatus = async (assignment, status) => {
    try {
      setSavingInline(assignment.id);
      const { data } = await updateAssignment(assignment.id, { status });
      setAssignments((prev) => prev.map((item) => (item.id === assignment.id ? data : item)));
      toast.success('Assignment status updated');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not update status');
      await loadData();
    } finally {
      setSavingInline('');
    }
  };

  const confirmDelete = (items, type = 'single') => {
    setPendingDelete({ type, assignments: items });
  };

  const handleConfirmedDelete = async () => {
    if (!pendingDelete) return;
    try {
      setDeleting(true);
      const ids = pendingDelete.assignments.map((assignment) => assignment.id);
      await Promise.all(ids.map((id) => deleteAssignment(id)));
      toast.success(`${ids.length} assignment${ids.length === 1 ? '' : 's'} deleted`);
      setPendingDelete(null);
      setSelectedIds(new Set());
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not delete assignments');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      id: 'title',
      header: 'Title',
      sortable: true,
      sortAccessor: (assignment) => assignment.title || '',
      cellClassName: 'min-w-[250px]',
      cell: (assignment) => (
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#062B1A]/5 flex items-center justify-center">
            <FileText className="w-4 h-4 text-[#062B1A]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#062B1A]">{assignment.title || 'Untitled assignment'}</p>
            <p className="text-xs text-[#062B1A]/45">{assignment.instructions ? 'Instructions added' : 'No instructions'}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'course',
      header: 'Course',
      sortable: true,
      sortAccessor: (assignment) => courseLabel(courseMap[assignment.course_id]),
      cellClassName: 'min-w-[240px] text-sm text-[#062B1A]/70',
      cell: (assignment) => courseLabel(courseMap[assignment.course_id]),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      cellClassName: 'min-w-[160px]',
      cell: (assignment) => (
        <InlineSelect
          value={assignment.status || 'draft'}
          disabled={savingInline === assignment.id}
          options={publishStatusOptions}
          onChange={(value) => handleInlineStatus(assignment, value)}
        />
      ),
    },
    {
      id: 'total_marks',
      header: 'Total marks',
      sortable: true,
      sortAccessor: (assignment) => Number(assignment.total_marks || 0),
      cellClassName: 'min-w-[130px] text-sm text-[#062B1A]/70',
      cell: (assignment) => assignment.total_marks || 0,
    },
    {
      id: 'due_date',
      header: 'Due date',
      sortable: true,
      cellClassName: 'min-w-[140px] text-sm text-[#062B1A]/70',
      cell: (assignment) => formatDate(assignment.due_date),
    },
  ];

  const rowActions = (assignment) => (
    <div className="flex justify-end gap-2">
      <Button type="button" variant="outline" size="sm" className="border-[#062B1A]/15 text-[#062B1A]" onClick={() => openEdit(assignment)}>
        <Pencil className="w-4 h-4 mr-2" />
        Edit
      </Button>
      <Button type="button" variant="outline" size="sm" className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800" onClick={() => confirmDelete([assignment])}>
        <Trash2 className="w-4 h-4 mr-2" />
        Delete
      </Button>
    </div>
  );

  const bulkActions = ({ selectedCount }) => (
    <Button
      type="button"
      size="sm"
      disabled={selectedCount === 0}
      className="bg-red-700 text-white hover:bg-red-800"
      onClick={() => confirmDelete(selectedAssignments, 'bulk')}
    >
      <Trash2 className="w-4 h-4 mr-2" />
      Bulk Delete
    </Button>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Manage Assignments</h2>
          <p className="text-sm text-[#062B1A]/60 mt-1">{assignments.length} assignment{assignments.length === 1 ? '' : 's'} in the current view</p>
        </div>
        <Button className="bg-[#062B1A] text-white hover:bg-[#041B10] btn-hover" onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Create Assignment
        </Button>
      </div>

      <DataTable
        title="Assignment Management"
        description="Click a row for the full assignment editor. Status changes save immediately."
        data={filteredAssignments}
        columns={columns}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search assignments or courses..."
        filters={[
          { id: 'course', value: courseFilter, onChange: setCourseFilter, options: [{ value: 'all', label: 'All courses' }, ...courses.map((course) => ({ value: course.id, label: courseLabel(course) }))] },
          { id: 'status', value: statusFilter, onChange: setStatusFilter, options: [{ value: 'all', label: 'All statuses' }, ...publishStatusOptions] },
        ]}
        selectedIds={selectedIds}
        onSelectedIdsChange={setSelectedIds}
        bulkActions={bulkActions}
        rowActions={rowActions}
        onRowClick={openEdit}
        emptyMessage="No assignments match the current filters."
        pageSize={8}
      />

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-display)' }}>{editing ? 'Edit' : 'Create'} Assignment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Title *</label>
              <Input value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} className="border-[rgba(6,43,26,0.12)]" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Course *</label>
              <Select value={form.course_id} onValueChange={(value) => setForm((prev) => ({ ...prev, course_id: value }))}>
                <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{courses.map((course) => <SelectItem key={course.id} value={course.id}>{courseLabel(course)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Description</label>
              <Textarea value={form.description} onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))} className="border-[rgba(6,43,26,0.12)]" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Instructions</label>
              <Textarea value={form.instructions} onChange={(event) => setForm((prev) => ({ ...prev, instructions: event.target.value }))} className="border-[rgba(6,43,26,0.12)]" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Due Date</label>
                <Input type="date" value={form.due_date} onChange={(event) => setForm((prev) => ({ ...prev, due_date: event.target.value }))} className="border-[rgba(6,43,26,0.12)]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Total Marks</label>
                <Input type="number" value={form.total_marks} onChange={(event) => setForm((prev) => ({ ...prev, total_marks: parseInt(event.target.value, 10) || 0 }))} className="border-[rgba(6,43,26,0.12)]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Status</label>
                <Select value={form.status} onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}>
                  <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue /></SelectTrigger>
                  <SelectContent>{publishStatusOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {/* Attachments hang off a saved assignment, so they appear once it
                exists rather than being lost if the form is abandoned. */}
            {editing ? (
              <div className="border-t border-[rgba(6,43,26,0.08)] pt-4">
                <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Brief and handouts</label>
                <CourseResources courseId={editing.course_id} assignmentId={editing.id} editable compact />
              </div>
            ) : (
              <p className="text-xs text-[#062B1A]/40">Create the assignment first, then reopen it to attach the brief.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" onClick={handleSave}>{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Delete ${pendingDelete?.assignments?.length || 0} assignment${pendingDelete?.assignments?.length === 1 ? '' : 's'}?`}
        description="This permanently removes the selected assignment records. Existing student submissions are not cascaded by this action."
        count={pendingDelete?.assignments?.length || 0}
        deleting={deleting}
        onConfirm={handleConfirmedDelete}
      />
    </div>
  );
}
